
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Skeleton,
  Badge,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  Construction,
  ShoppingCart,
  PhotoLibrary,
  Event,
  ArrowForward,
  Assignment
} from '@mui/icons-material';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useObras } from '../context/ObrasContext';
import { Material, Foto } from '../types';
import { collection, query, orderBy, limit, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format, isSameDay, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { usuario, logout } = useAuth();
  const { obras } = useObras();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data for lists
  const [lastPhotos, setLastPhotos] = useState<Foto[]>([]);
  const [lastMaterials, setLastMaterials] = useState<Material[]>([]);

  // Data for summary cards
  const [totalPendingMaterials, setTotalPendingMaterials] = useState(0);
  const [totalPendingTasks, setTotalPendingTasks] = useState(0);
  const [recentPhotosCount, setRecentPhotosCount] = useState(0);

  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [loadingMaterials, setLoadingMaterials] = useState(true);

  // Calendar State
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dayCounts, setDayCounts] = useState({
    fotos: 0,
    materiales: 0,
    tareas: 0
  });
  const [loadingDayCounts, setLoadingDayCounts] = useState(false);

  // State for chunked results (Restricted users)
  const [materialsChunks, setMaterialsChunks] = useState<Material[][]>([]);
  const [tareasChunks, setTareasChunks] = useState<number[]>([]);
  const [fotosCountChunks, setFotosCountChunks] = useState<number[]>([]);
  const [fotosListChunks, setFotosListChunks] = useState<Foto[][]>([]);

  // Obtener fechas importantes de las obras
  const fechasImportantes = obras
    .filter(obra => obra.estado === 'activa')
    .reduce((dates: Date[], obra) => {
      dates.push(obra.fechaInicio);
      dates.push(obra.fechaLimite);
      return dates;
    }, []);

  useEffect(() => {
    if (!usuario) return;

    setLoadingPhotos(true);
    setLoadingMaterials(true);

    const esAdmin = usuario.role === 'ADMIN';
    const puedeVerTodas = usuario.puedeVerTodasLasObras;
    const obrasAsignadas = usuario.obrasAsignadas || [];

    let unsubscribeFuncs: (() => void)[] = [];

    const setupListeners = () => {
      // Helper to chunk array
      const chunkArray = (arr: string[], size: number) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      // 1. MATERIALES (List & Count)
      // We need two things: 
      // a) Total count of pending materials (for card)
      // b) Last 5 pending materials (for list)

      // For simplicity and realtime updates without excessive reads, we'll query pending materials.
      // If the dataset is huge, this might be costly, but for now it's fine.

      if (esAdmin || puedeVerTodas) {
        // ADMIN: Query all pending materials
        const materialesQuery = query(
          collection(db, 'materiales'),
          where('comprado', '==', false)
        );

        const unsubMateriales = onSnapshot(materialesQuery, (snapshot) => {
          const allMaterials = snapshot.docs.map(doc => {
            const data = doc.data() as any;
            const obra = obras.find(o => o.id === data.obraId);
            return {
              id: doc.id,
              ...data,
              obraNombre: obra?.nombre || 'Obra no encontrada',
              fechaCreacion: data.fechaCreacion?.toDate() || new Date(),
              fechaRequerida: data.fechaRequerida?.toDate() || new Date()
            };
          }) as Material[];

          setTotalPendingMaterials(allMaterials.length);
          // Sort by date if needed, or just take first 5
          setLastMaterials(allMaterials.slice(0, 5));
          setLoadingMaterials(false);
        }, (error) => {
          console.error("Error fetching materials:", error);
          setLoadingMaterials(false);
        });
        unsubscribeFuncs.push(unsubMateriales);

        // TAREAS (Pending - Hoy)
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const tareasQuery = query(
          collection(db, 'tareas'),
          where('completada', '==', false),
          where('completada', '==', false),
          // Remove startOfDay filter to include overdue tasks
          where('fechaProgramada', '<=', Timestamp.fromDate(endOfDay))
        );
        const unsubTareas = onSnapshot(tareasQuery, (snapshot) => {
          setTotalPendingTasks(snapshot.size);
        });
        unsubscribeFuncs.push(unsubTareas);

        // FOTOS (Recent 7 days)
        const sevenDaysAgo = subDays(new Date(), 7);
        const fotosQuery = query(
          collection(db, 'fotos'),
          where('fechaSubida', '>=', Timestamp.fromDate(sevenDaysAgo)),
          orderBy('fechaSubida', 'desc')
        );
        const unsubFotos = onSnapshot(fotosQuery, (snapshot) => {
          setRecentPhotosCount(snapshot.size);
          // Also update last photos list from this if we want, or separate query.
          // The original code had a separate query for last 4 photos overall.
          // Let's keep the logic simple: use this for count, and maybe reuse for list if it matches criteria.
          // But the list shows "Last Photos" regardless of time? No, usually just recent ones.
          // Let's stick to the requirement: Card shows count of last 7 days.
          // The list shows "Últimas Fotos" (limit 4).
          // We can do a separate query for the list if we want to be precise, or just use this one if we assume "Last Photos" usually implies recent.
          // However, if no photos in 7 days, list would be empty. Original code showed last 4 regardless of time.
          // Let's keep the original "Last 4" query for the list, and a separate one for the count? 
          // Or just query last 4 for list, and query count for 7 days.

          // Query for list (Last 4)
          // We can't easily combine "limit 4" and "count all in 7 days" in one query without client side filtering of all.
          // Let's do client side filtering of "Last 7 days" from a larger query? No, that's bad.
          // Let's run two queries for now to be safe and correct.
        });
        unsubscribeFuncs.push(unsubFotos);

        // Separate query for Last 4 Photos (List)
        const lastFotosQuery = query(
          collection(db, 'fotos'),
          orderBy('fechaSubida', 'desc'),
          limit(4)
        );
        const unsubLastFotos = onSnapshot(lastFotosQuery, (snapshot) => {
          const fotosData = snapshot.docs.map(doc => {
            const data = doc.data();
            const obra = obras.find(o => o.id === data.obraId);
            return {
              id: doc.id,
              ...data,
              fechaSubida: data.fechaSubida.toDate(),
              obraNombre: obra?.nombre || 'Obra no encontrada'
            };
          }) as Foto[];
          setLastPhotos(fotosData);
          setLoadingPhotos(false);
        });
        unsubscribeFuncs.push(unsubLastFotos);

      } else {
        // RESTRICTED USER
        if (obrasAsignadas.length === 0) {
          setLastPhotos([]);
          setLastMaterials([]);
          setTotalPendingMaterials(0);
          setTotalPendingTasks(0);
          setRecentPhotosCount(0);
          setLoadingPhotos(false);
          setLoadingMaterials(false);
          return;
        }

        const chunks = chunkArray(obrasAsignadas, 10);

        // --- MATERIALES ---
        const matUnsubs = chunks.map((chunk, index) => {
          const q = query(
            collection(db, 'materiales'),
            where('obraId', 'in', chunk),
            where('comprado', '==', false)
          );
          return onSnapshot(q, (snapshot) => {
            setMaterialsChunks(prev => {
              const newChunks = [...prev];
              newChunks[index] = snapshot.docs.map(doc => {
                const data = doc.data() as any;
                const obra = obras.find(o => o.id === data.obraId);
                return {
                  id: doc.id,
                  ...data,
                  obraNombre: obra?.nombre || 'Obra no encontrada',
                  fechaCreacion: data.fechaCreacion?.toDate() || new Date(),
                  fechaRequerida: data.fechaRequerida?.toDate() || new Date()
                };
              });
              return newChunks;
            });
          });
        });
        unsubscribeFuncs.push(...matUnsubs);

        // --- TAREAS ---
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const tareasUnsubs = chunks.map((chunk, index) => {
          const q = query(
            collection(db, 'tareas'),
            where('obraId', 'in', chunk),
            where('completada', '==', false),
            where('completada', '==', false),
            // Remove startOfDay filter to include overdue tasks
            where('fechaProgramada', '<=', Timestamp.fromDate(endOfDay))
          );
          return onSnapshot(q, (snapshot) => {
            setTareasChunks(prev => {
              const newChunks = [...prev];
              newChunks[index] = snapshot.size; // Just need count
              return newChunks;
            });
          });
        });
        unsubscribeFuncs.push(...tareasUnsubs);

        // --- FOTOS (Count 7 days) ---
        const sevenDaysAgo = subDays(new Date(), 7);
        const fotosCountUnsubs = chunks.map((chunk, index) => {
          const q = query(
            collection(db, 'fotos'),
            where('obraId', 'in', chunk),
            where('fechaSubida', '>=', Timestamp.fromDate(sevenDaysAgo))
          );
          return onSnapshot(q, (snapshot) => {
            setFotosCountChunks(prev => {
              const newChunks = [...prev];
              newChunks[index] = snapshot.size;
              return newChunks;
            });
          });
        });
        unsubscribeFuncs.push(...fotosCountUnsubs);

        // --- FOTOS (List - Last 4) ---
        // This is harder with chunks because we need global sort.
        // We'll fetch last 4 from EACH chunk, then merge and sort in memory, then take top 4.
        const fotosListUnsubs = chunks.map((chunk, index) => {
          const q = query(
            collection(db, 'fotos'),
            where('obraId', 'in', chunk),
            orderBy('fechaSubida', 'desc'),
            limit(4)
          );
          return onSnapshot(q, (snapshot) => {
            setFotosListChunks(prev => {
              const newChunks = [...prev];
              newChunks[index] = snapshot.docs.map(doc => {
                const data = doc.data();
                const obra = obras.find(o => o.id === data.obraId);
                return {
                  id: doc.id,
                  ...data,
                  fechaSubida: data.fechaSubida.toDate(),
                  obraNombre: obra?.nombre || 'Obra no encontrada'
                };
              }) as Foto[];
              return newChunks;
            });
          });
        });
        unsubscribeFuncs.push(...fotosListUnsubs);
      }
    };

    setupListeners();
    setIsLoading(false);

    return () => {
      unsubscribeFuncs.forEach(fn => fn());
    };
  }, [usuario, obras]);

  // Effect for Selected Date Counts
  useEffect(() => {
    if (!usuario) return;

    setLoadingDayCounts(true);

    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    const esAdmin = usuario.role === 'ADMIN';
    const puedeVerTodas = usuario.puedeVerTodasLasObras;
    const obrasAsignadas = usuario.obrasAsignadas || [];

    let unsubscribeFuncs: (() => void)[] = [];

    const setupDayListeners = () => {
      // Helper to chunk array
      const chunkArray = (arr: string[], size: number) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      if (esAdmin || puedeVerTodas) {
        // --- ADMIN / ALL ---

        // Fotos
        const fotosQuery = query(
          collection(db, 'fotos'),
          where('fechaSubida', '>=', Timestamp.fromDate(startOfDay)),
          where('fechaSubida', '<=', Timestamp.fromDate(endOfDay))
        );
        const unsubFotos = onSnapshot(fotosQuery, (snapshot) => {
          setDayCounts(prev => ({ ...prev, fotos: snapshot.size }));
        });
        unsubscribeFuncs.push(unsubFotos);

        // Materiales (Created on this day)
        const materialesQuery = query(
          collection(db, 'materiales'),
          where('fechaCreacion', '>=', Timestamp.fromDate(startOfDay)),
          where('fechaCreacion', '<=', Timestamp.fromDate(endOfDay))
        );
        const unsubMateriales = onSnapshot(materialesQuery, (snapshot) => {
          setDayCounts(prev => ({ ...prev, materiales: snapshot.size }));
        });
        unsubscribeFuncs.push(unsubMateriales);

        // Tareas (Created on this day)
        const tareasQuery = query(
          collection(db, 'tareas'),
          where('fechaCreacion', '>=', Timestamp.fromDate(startOfDay)),
          where('fechaCreacion', '<=', Timestamp.fromDate(endOfDay))
        );
        const unsubTareas = onSnapshot(tareasQuery, (snapshot) => {
          setDayCounts(prev => ({ ...prev, tareas: snapshot.size }));
        });
        unsubscribeFuncs.push(unsubTareas);

        setLoadingDayCounts(false);

      } else {
        // --- RESTRICTED ---
        if (obrasAsignadas.length === 0) {
          setDayCounts({ fotos: 0, materiales: 0, tareas: 0 });
          setLoadingDayCounts(false);
          return;
        }

        const chunks = chunkArray(obrasAsignadas, 10);

        // Initialize chunks state
        setDayCountsChunks({
          fotos: new Array(chunks.length).fill(0),
          materiales: new Array(chunks.length).fill(0),
          tareas: new Array(chunks.length).fill(0)
        });

        chunks.forEach((chunk: string[], index: number) => {
          // Fotos
          const qFotos = query(
            collection(db, 'fotos'),
            where('obraId', 'in', chunk),
            where('fechaSubida', '>=', Timestamp.fromDate(startOfDay)),
            where('fechaSubida', '<=', Timestamp.fromDate(endOfDay))
          );
          unsubscribeFuncs.push(onSnapshot(qFotos, (snap) => {
            setDayCountsChunks(prev => {
              const newArr = [...prev.fotos];
              newArr[index] = snap.size;
              return { ...prev, fotos: newArr };
            });
          }));

          // Materiales
          const qMat = query(
            collection(db, 'materiales'),
            where('obraId', 'in', chunk),
            where('fechaCreacion', '>=', Timestamp.fromDate(startOfDay)),
            where('fechaCreacion', '<=', Timestamp.fromDate(endOfDay))
          );
          unsubscribeFuncs.push(onSnapshot(qMat, (snap) => {
            setDayCountsChunks(prev => {
              const newArr = [...prev.materiales];
              newArr[index] = snap.size;
              return { ...prev, materiales: newArr };
            });
          }));

          // Tareas
          const qTareas = query(
            collection(db, 'tareas'),
            where('obraId', 'in', chunk),
            where('fechaCreacion', '>=', Timestamp.fromDate(startOfDay)),
            where('fechaCreacion', '<=', Timestamp.fromDate(endOfDay))
          );
          unsubscribeFuncs.push(onSnapshot(qTareas, (snap) => {
            setDayCountsChunks(prev => {
              const newArr = [...prev.tareas];
              newArr[index] = snap.size;
              return { ...prev, tareas: newArr };
            });
          }));
        });

        setLoadingDayCounts(false);
      }
    };

    setupDayListeners();

    return () => {
      unsubscribeFuncs.forEach(fn => fn());
    };
  }, [selectedDate, usuario, obras]);

  // State for Day Counts Chunks (Restricted)
  const [dayCountsChunks, setDayCountsChunks] = useState<{
    fotos: number[],
    materiales: number[],
    tareas: number[]
  }>({ fotos: [], materiales: [], tareas: [] });

  // Effect to aggregate Day Counts Chunks
  useEffect(() => {
    if (usuario?.role === 'ADMIN' || usuario?.puedeVerTodasLasObras) return;

    setDayCounts({
      fotos: dayCountsChunks.fotos.reduce((a, b) => a + b, 0),
      materiales: dayCountsChunks.materiales.reduce((a, b) => a + b, 0),
      tareas: dayCountsChunks.tareas.reduce((a, b) => a + b, 0)
    });
  }, [dayCountsChunks, usuario]);

  // Effect to aggregate chunk results (Restricted users - Main Dashboard)
  useEffect(() => {
    if (usuario?.role === 'ADMIN' || usuario?.puedeVerTodasLasObras) return;

    // Materials
    const allMats = materialsChunks.flat();
    setTotalPendingMaterials(allMats.length);
    setLastMaterials(allMats.slice(0, 5)); // Simple slice, ideally sort by date
    setLoadingMaterials(false);

    // Tasks
    const totalTasks = tareasChunks.reduce((a, b) => a + b, 0);
    setTotalPendingTasks(totalTasks);

    // Photos Count
    const totalPhotos = fotosCountChunks.reduce((a, b) => a + b, 0);
    setRecentPhotosCount(totalPhotos);

    // Photos List
    const allPhotos = fotosListChunks.flat();
    const sortedPhotos = allPhotos.sort((a, b) => b.fechaSubida.getTime() - a.fechaSubida.getTime());
    setLastPhotos(sortedPhotos.slice(0, 4));
    setLoadingPhotos(false);

  }, [materialsChunks, tareasChunks, fotosCountChunks, fotosListChunks, usuario]);

  const tileContent = ({ date }: { date: Date }) => {
    const fechasHoy = fechasImportantes.filter(d => isSameDay(d, date));
    if (fechasHoy.length > 0) {
      return (
        <Badge
          color="primary"
          variant="dot"
          sx={{
            '& .MuiBadge-badge': {
              right: '45%',
              top: '15%'
            }
          }}
        />
      );
    }
    return null;
  };

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {/* Materiales Pendientes */}
      <Grid item xs={12} sm={6} md={4}>
        <Card
          onClick={() => navigate('/materiales')}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(237, 108, 2, 0.25)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
            <ShoppingCart />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {totalPendingMaterials}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Materiales Pendientes
            </Typography>
          </Box>
        </Card>
      </Grid>

      {/* Tareas Pendientes Hoy */}
      <Grid item xs={12} sm={6} md={4}>
        <Card
          onClick={() => navigate('/tareas')}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(156, 39, 176, 0.25)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
            <Assignment />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {totalPendingTasks}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Tareas Pendientes
            </Typography>
          </Box>
        </Card>
      </Grid>

      {/* Fotos Recientes */}
      <Grid item xs={12} sm={6} md={4}>
        <Card
          onClick={() => navigate('/fotos')}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(46, 125, 50, 0.25)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
            <PhotoLibrary />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {recentPhotosCount}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Fotos (últimos 7 días)
            </Typography>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );

  const renderDashboard = () => {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">
            Panel de Control
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Bienvenido de nuevo, {usuario?.displayName || 'Usuario'}
          </Typography>
        </Box>

        {renderSummaryCards()}

        <Grid container spacing={3}>
          {/* Calendario */}
          <Grid item xs={12} md={12} lg={4}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                  <Event sx={{ mr: 1 }} /> Calendario
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  p: 2,
                  '.react-calendar': {
                    width: '100%',
                    border: 'none',
                    fontFamily: 'inherit',
                    boxShadow: 'none',
                    bgcolor: 'background.paper',
                  },
                  '.react-calendar__tile': {
                    padding: '10px 6px',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    '&:enabled:hover': {
                      bgcolor: 'action.hover'
                    }
                  },
                  '.react-calendar__tile--now': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                    '&:enabled:hover': {
                      bgcolor: 'primary.main'
                    }
                  },
                  '.react-calendar__tile--active': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:enabled:hover': {
                      bgcolor: 'primary.dark'
                    }
                  },
                  '.react-calendar__month-view__days__day--weekend': {
                    color: 'error.main'
                  },
                  '.react-calendar__navigation button': {
                    minWidth: 44,
                    background: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }
                }}>
                  <Calendar
                    locale="es"
                    value={selectedDate}
                    onChange={(value) => setSelectedDate(value as Date)}
                    tileContent={tileContent}
                  />
                </Box>
                <Divider />
                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
                    Resumen del {format(selectedDate, "d 'de' MMMM", { locale: es })}
                  </Typography>

                  {loadingDayCounts ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Skeleton variant="rectangular" width={80} height={60} />
                      <Skeleton variant="rectangular" width={80} height={60} />
                      <Skeleton variant="rectangular" width={80} height={60} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                          <PhotoLibrary color="primary" sx={{ mb: 0.5 }} />
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {dayCounts.fotos}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fotos
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                          <ShoppingCart color="warning" sx={{ mb: 0.5 }} />
                          <Typography variant="h6" fontWeight="bold" color="warning.main">
                            {dayCounts.materiales}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Materiales
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                          <Assignment color="secondary" sx={{ mb: 0.5 }} />
                          <Typography variant="h6" fontWeight="bold" color="secondary.main">
                            {dayCounts.tareas}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tareas
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Galería de Fotos */}
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                  <PhotoLibrary sx={{ mr: 1, color: 'primary.main' }} /> Últimas Fotos
                </Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/fotos')}>
                  Ver todas
                </Button>
              </Box>
              <Divider />
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                {loadingPhotos ? (
                  <Grid container spacing={1}>
                    {[1, 2, 3, 4].map((item) => (
                      <Grid item xs={6} key={item}>
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                      </Grid>
                    ))}
                  </Grid>
                ) : lastPhotos.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    <PhotoLibrary sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">No hay fotos recientes</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {lastPhotos.map((foto, index) => (
                      <Grid item xs={6} key={foto.id || `foto-${index}`}>
                        <Card
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.02)' }
                          }}
                          onClick={() => navigate('/fotos')}
                        >
                          <CardMedia
                            component="img"
                            height="120"
                            image={foto.url}
                            alt={foto.descripcion}
                          />
                          <Box sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            p: 0.5,
                            px: 1
                          }}>
                            <Typography variant="caption" display="block" noWrap>
                              {foto.obraNombre}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Materiales a Comprar */}
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                  <ShoppingCart sx={{ mr: 1, color: 'warning.main' }} /> Por Comprar
                </Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/materiales')}>
                  Ver todos
                </Button>
              </Box>
              <Divider />
              <CardContent sx={{ p: 0, flexGrow: 1 }}>
                {loadingMaterials ? (
                  <Box sx={{ p: 2 }}>
                    {[1, 2, 3].map((item) => (
                      <Box key={item} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="40%" />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : lastMaterials.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    <ShoppingCart sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">No hay materiales pendientes</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {lastMaterials.map((material, index) => (
                      <React.Fragment key={material.id || `material-${index}`}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate('/materiales')}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>
                              <Construction fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight="bold">
                                {material.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography component="span" variant="caption" color="text.primary">
                                  {material.obraNombre}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary">
                                  Cant: {material.cantidad} • {format(material.fechaRequerida, "dd MMM", { locale: es })}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip
                            label="Pendiente"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </ListItem>
                        {index < lastMaterials.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  // Redirección para contratistas
  if (usuario?.role === 'contratista') {
    navigate('/materiales');
    return null;
  }

  if (usuario?.role === 'no asignado') {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 600,
          mx: 'auto',
          mt: 4,
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Typography variant="h5" gutterBottom color="primary">
          ¡Bienvenido a Mat ByB!
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Su cuenta está pendiente de asignación de rol. Por favor, espere hasta que un administrador revise y configure su cuenta.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Una vez que se le asigne un rol, podrá acceder a las funcionalidades del sistema.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={logout}
          sx={{ mt: 2 }}
        >
          Cerrar Sesión
        </Button>
      </Paper>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {renderDashboard()}
      </Box>
    </Layout>
  );
};
